/**
 * Canvas2D strip drawer for the pseudo-3D road renderer.
 *
 * Consumes a `Strip[]` produced by `segmentProjector.project` and walks
 * back-to-front, drawing the sky band once, then each visible strip pair
 * (grass, rumble, road, lane markings) as filled trapezoids.
 *
 * Parallax support is opt-in via `DrawRoadOptions.parallax`: when present,
 * the drawer paints the parallax bands instead of the flat sky gradient,
 * preserving the painter's algorithm (background first, road over).
 *
 * Texture-backed sprite atlases land in `spriteAtlas.ts`; this drawer
 * owns the procedural roadside billboard fallback so compiled roadside
 * ids are visible before binary art ships.
 *
 * The drawer is the only module that knows about a Canvas2D context. The
 * projector is pure so unit tests can run without jsdom canvas mocking.
 */

import {
  DEFAULT_COLORS,
  GRASS_STRIPE_LEN,
  LANE_STRIPE_LEN,
  RUMBLE_STRIPE_LEN,
  SEGMENT_LENGTH,
  SPRITE_BASE_SCALE,
} from "@/road/constants";
import type { Camera, Strip, Viewport } from "@/road/types";
import type { RegionPalette } from "@/data/palettes";
import type { WeatherOption } from "@/data/schemas";
import { visibilityForWeather } from "@/game/weather";

import { drawDust, type DustState } from "./dust";
import { drawSpeedLines, type SpeedLineState } from "./speedLines";
import type { PaletteCache } from "./paletteCache";
import { paletteCacheKey, recolourFrameToImageBitmap } from "./paletteRecolour";
import { drawParallax, type ParallaxLayer } from "./parallax";
import type { ProjectedPickupSprite } from "./pickupSprites";
import {
  resolveCarRenderFrames,
  selectCarFramePlan,
  type CarSpriteSet,
} from "./carSpriteCompositor";
import { frame, spriteCanRecolour, spriteFrameCount, type LoadedAtlas } from "./spriteAtlas";
export {
  TUNNEL_ADAPTATION_HIGHLIGHT_FILL,
  TUNNEL_ADAPTATION_HIGHLIGHT_MAX_ALPHA,
  TUNNEL_ADAPTATION_MAX_ALPHA,
  TUNNEL_ADAPTATION_OVERLAY_FILL,
} from "./tunnelRenderer";
import { drawTunnelAdaptation } from "./tunnelRenderer";
import { drawVfx, type VfxState } from "./vfx";

/**
 * Default alpha for the ghost car overlay. Pinned by the F-022 dot
 * stress-test item 9: a translucent second car following the player's
 * recorded path so the live driver can see their best line without the
 * ghost occluding the road. Kept as a named constant so the §6 Time Trial
 * UI can dim or brighten the ghost from a settings toggle without
 * re-pinning the magic number at every call site.
 */
export const GHOST_CAR_DEFAULT_ALPHA = 0.5;

/**
 * Default fill colour for the ghost car placeholder rectangle. The §6
 * Time Trial spec calls out a desaturated / blue tint to differentiate
 * the ghost from the live car; we ship the blue tint as the default so
 * the placeholder reads as "other player" without the §17 sprite atlas
 * being wired in. The atlas-frame variant lands in the same slice that
 * threads `LoadedAtlas` into the renderer (followup F-022 consumer side).
 */
export const GHOST_CAR_DEFAULT_FILL = "#5fb6ff";

/**
 * Default live player-car overlay colours. These are placeholder fills
 * until the §16 / §17 atlas slice wires real directional sprites through
 * the renderer.
 */
export const PLAYER_CAR_DEFAULT_FILL = "#f2c94c";
export const PLAYER_CAR_DEFAULT_SHADOW = "rgba(0, 0, 0, 0.55)";
export const PLAYER_CAR_DEFAULT_WINDSHIELD = "#18243d";
export const PLAYER_CAR_DEFAULT_TIRE = "#10151f";
export const PLAYER_CAR_DEFAULT_TAIL_LIGHT = "#ff3d38";
export const AI_CAR_DEFAULT_ALPHA = 0.95;
export const AI_CAR_DEFAULT_FILL = "#ff6b5f";
export const AI_CAR_DEFAULT_SHADOW = "rgba(0, 0, 0, 0.45)";

/**
 * Standard player-car footprint. §16 pins the player car at 16 to 22
 * percent of screen height in the standard camera mode; 0.18 leaves
 * headroom for the HUD and minimap while reading clearly at 800x480.
 */
export const PLAYER_CAR_HEIGHT_FRACTION = 0.18;
export const PLAYER_CAR_WIDTH_TO_HEIGHT = 1.15;
export const PLAYER_CAR_DEFAULT_SPRITE_ID = "sparrow_clean";
export const ROADSIDE_DRAW_PERIOD = 10;
/**
 * Maximum on-screen height a roadside prop may take, as a fraction of the
 * viewport height. Iter-5 / Q-017 dropped this from 0.22 to 0.18 so close-in
 * props clamp at exactly the player car silhouette (`PLAYER_CAR_HEIGHT_FRACTION
 * = 0.18`); pre-fix a near-field tree could draw at 74% viewport, dwarfing
 * the player. The clamp pairs with the per-kind heightRoadFactor retune
 * below; either alone would still leave outliers (fence_post at 2x correct
 * height, or mid-far trees still dwarfing the silhouette).
 */
export const ROADSIDE_MAX_HEIGHT_FRACTION = 0.18;
export const WEATHER_EFFECT_REDUCTION_SCALE = 0.35;
export const HEAT_SHIMMER_FILL = "#f4ddb0";
export const HEAT_SHIMMER_MAX_ALPHA = 0.16;
export const HEAT_SHIMMER_BAND_COUNT = 6;
export const RAIN_ROAD_SHEEN_FILL = "#d8f4ff";
export const RAIN_ROAD_SHEEN_MAX_ALPHA = 0.16;
export const SNOW_ROADSIDE_WHITENING_FILL = "#edf7ff";
export const SNOW_ROADSIDE_WHITENING_MAX_ALPHA = 0.2;
export const PICKUP_CASH_FILL = "#ffd84a";
export const PICKUP_CASH_SHADOW = "#7a4b00";
export const PICKUP_NITRO_FILL = "#55d8ff";
export const PICKUP_NITRO_SHADOW = "#173f82";
export const PICKUP_FEEDBACK_TTL_MS = 900;
const LANE_DASH_CYCLE_METERS = LANE_STRIPE_LEN * SEGMENT_LENGTH;
const LANE_DASH_VISIBLE_METERS = SEGMENT_LENGTH * 2;

export interface RoadColors {
  skyTop: string;
  skyBottom: string;
  grassLight: string;
  grassDark: string;
  rumbleLight: string;
  rumbleDark: string;
  roadLight: string;
  roadDark: string;
  lane: string;
  finishLight: string;
  finishDark: string;
}

export interface DrawRoadOptions {
  colors?: RoadColors;
  /**
   * Optional parallax bands. When present, the drawer renders the bands
   * instead of the flat sky gradient. Pass an empty array to skip the
   * sky entirely (useful for dev pages that test road-only behaviour).
   * The accompanying `camera` is required so the parallax module can
   * scroll horizontally.
   */
  parallax?: {
    layers: readonly ParallaxLayer[];
    camera: Pick<Camera, "x" | "z">;
  };
  /**
   * Camera world-z used for foreground road-marking phase. The live race
   * route gets this from the same camera used for projection. Dev callers
   * can omit it; non-foreground strips still phase from compiled worldZ.
   */
  markingCameraZ?: number;
  /**
   * Optional VFX state. When present, the drawer paints active flashes
   * over the parallax / sky band, then translates the canvas by the
   * summed shake offset before drawing the road strips so the entire
   * road frame shakes as one unit. The flash lands BEHIND the road on
   * purpose: a HUD-style overlay flash belongs in the UI layer, while
   * an in-world impact flash should not occlude the player car.
   */
  vfx?: VfxState;
  /**
   * Optional dust pool. Painted AFTER the road strips so particles sit
   * over the grass / road, matching the §16 "Dust roost" reference (the
   * plume rises above the surface, never under it). Production sites
   * tick the pool from the sim layer with the player's surface flag and
   * pass it in here; the drawer never advances the pool itself.
   */
  dust?: DustState;
  /**
   * Optional radial speed-line pool for §16 "Strong foreground speed
   * cues". Painted above road / dust / weather but below the player
   * car so streaks read as foreground motion without occluding the
   * driver. Caller owns the pool; the drawer never advances it.
   */
  speedLines?: SpeedLineState;
  /**
   * Optional screen-space weather effects for §14 / §16. These are
   * visual only: physics weather is handled by `src/game/raceSession.ts`.
   * The accessibility flag reduces particle density and overlay alpha
   * while preserving enough feedback to show the selected weather.
   */
  weatherEffects?: {
    weather: WeatherOption;
    visualReduction?: boolean;
    particleIntensity?: number;
    reducedGlare?: boolean;
    highContrastRoadsideSigns?: boolean;
    fogFloorClamp?: number;
    flashReduction?: boolean;
  };
  /**
   * Optional tunnel light-adaptation pass. Authored tunnel hazard IDs are
   * compiled onto strips, and the renderer darkens the world view when a
   * visible tunnel strip is in the projected road. This keeps tunnel
   * metadata visual-only while collision logic continues to ignore tunnel
   * hazards.
   */
  tunnelAdaptation?: {
    enabled?: boolean;
    intensityScale?: number;
  };
  /**
   * Optional desert-tour heat shimmer pass for §14. The caller passes
   * a deterministic phase from camera z so the bands drift with motion
   * without consuming random state or changing physics.
   */
  heatShimmer?: {
    enabled?: boolean;
    intensity?: number;
    phaseMeters?: number;
  };
  /**
   * Performance knob from §27. `1` draws the authored roadside cadence;
   * lower values skip a deterministic subset of billboard opportunities.
   */
  spriteDensityFactor?: number;
  /**
   * Optional atlas-backed roadside prop rendering. Recolourable atlas
   * entries use the supplied region palette and cache when available,
   * then fall back to the raw atlas frame while the recolour target is
   * prepared for a later frame.
   */
  roadsideAtlas?: {
    atlas: LoadedAtlas;
    palette?: RegionPalette;
    cache?: PaletteCache<CanvasImageSource>;
    inFlight?: Set<string>;
    failed?: Set<string>;
    onRecolourError?: (key: string, error: unknown) => void;
  } | null;
  pickupSprites?: readonly ProjectedPickupSprite[] | null;
  pickupFeedback?: {
    kind: "cash" | "nitro";
    ageMs: number;
  } | null;
  /**
   * Optional ghost car overlay per F-022. The §6 Time Trial flow drives
   * a second physics step from the recorded `Player.readNext` inputs to
   * derive the ghost's world position, projects it to the screen with
   * the same camera the live road uses, and passes the resulting
   * placement here. The drawer paints a translucent placeholder rect
   * BEHIND the dust pool (so off-road dust still occludes the ghost) but
   * AFTER the road strips (so the ghost reads as "on the road" rather
   * than driving through it). The atlas-frame draw variant lands when
   * the F-022 consumer slice threads a `LoadedAtlas` reference through
   * the renderer; until then the placeholder rect is the contract.
   *
   * `screenX` / `screenY` are CSS pixels (the render-time coordinates the
   * caller already computed via `segmentProjector.project`); `screenW` is
   * the projected car width in CSS pixels (commonly `strip.screenW *
   * carWidthFraction`); `alpha` defaults to `GHOST_CAR_DEFAULT_ALPHA`
   * when omitted. `null` / `undefined` skips the draw entirely so a fresh
   * Time Trial run with no stored ghost paints nothing extra.
   */
  ghostCar?: {
    screenX: number;
    screenY: number;
    screenW: number;
    alpha?: number;
    fill?: string;
    atlas?: LoadedAtlas | null;
    spriteId?: string;
    frameIndex?: number;
  } | null;
  /**
   * Optional opponent car overlays. Callers project these with the same
   * camera and strips as the road, then the drawer paints them in depth
   * order over the road and under the fixed player car.
   */
  aiCars?: readonly {
    screenX: number;
    screenY: number;
    screenW: number;
    depthMeters: number;
    alpha?: number;
    fill?: string;
    atlas?: LoadedAtlas | null;
    frameIndex?: number;
    braking?: boolean;
    nitroActive?: boolean;
    speedMetersPerSecond?: number;
    damageTotal?: number;
    spriteSet?: CarSpriteSet;
  }[] | null;
  /**
   * Optional live player car overlay. Pseudo-3D road strips represent the
   * world moving under a chase camera, so the player car is drawn as a
   * fixed bottom-screen overlay after the road and dust layers. The
   * placeholder is intentionally simple until atlas sprites land, but it
   * preserves the §16 standard camera footprint.
   */
  playerCar?: {
    fill?: string;
    shadow?: string;
    tire?: string;
    tailLight?: string;
    windshield?: string;
    weather?: WeatherOption;
    atlas?: LoadedAtlas | null;
    spriteId?: string;
    frameIndex?: number;
    braking?: boolean;
    nitroActive?: boolean;
    speedMetersPerSecond?: number;
    damageTotal?: number;
    spriteSet?: CarSpriteSet;
  } | null;
}

type RoadsideSpriteKind = "sign" | "tree" | "fence" | "rock" | "pole";

interface RoadsideSpriteStyle {
  kind: RoadsideSpriteKind;
  widthToHeight: number;
  heightRoadFactor: number;
  minHeight: number;
}

/**
 * Per-kind sprite style table, with `heightRoadFactor` tuned to the physical
 * height of the prop divided by the road half-width (4.5 m). Q-017 recommended
 * defaults adopted on 2026-05-06:
 *
 *   tree_pine:    10 m -> 2.22
 *   palms_sparse:  8 m -> 1.78
 *   light_pole:    9 m -> 2.00
 *   sign_marker:   3 m -> 0.67
 *   marina_signs:  3.5 m -> 0.78
 *   heat_sign:     3 m -> 0.67
 *   fence_post:    0.7 m -> 0.16
 *   guardrail:     0.7 m -> 0.16
 *   rock_boulder:  1.5 m -> 0.33
 *   water_wall:    1.0 m -> 0.22
 *   rock_spire:    6 m -> 1.33
 *
 * The pre-fix table read fence and guardrail at 0.5 (about 2.25 m physical),
 * roughly 3x the correct 0.7 m height; trees at 1.35 (6 m physical) about
 * 60% of the correct 10 m height. Combined with the 0.18 maxHeight clamp,
 * close-in props now match the player-car silhouette and far props read at
 * physically plausible scale.
 *
 * `minHeight` was previously a per-kind floor of 5 to 14 px. Far-away props
 * snapped up to that floor at the road's vanishing point, where many props
 * project to nearly the same screen X, and the result was a "totem pole"
 * stack of stacked sprites at the horizon. The floor is now 1 px so far
 * props dissolve to a pixel and the perspective reads cleanly.
 */
const ROADSIDE_SPRITE_STYLES: Record<string, RoadsideSpriteStyle> = {
  sign_marker: { kind: "sign", widthToHeight: 0.45, heightRoadFactor: 0.67, minHeight: 1 },
  tree_pine: { kind: "tree", widthToHeight: 0.58, heightRoadFactor: 2.22, minHeight: 1 },
  fence_post: { kind: "fence", widthToHeight: 0.32, heightRoadFactor: 0.16, minHeight: 1 },
  rock_boulder: { kind: "rock", widthToHeight: 1.2, heightRoadFactor: 0.33, minHeight: 1 },
  light_pole: { kind: "pole", widthToHeight: 0.16, heightRoadFactor: 2.00, minHeight: 1 },
  palms_sparse: { kind: "tree", widthToHeight: 0.58, heightRoadFactor: 1.78, minHeight: 1 },
  marina_signs: { kind: "sign", widthToHeight: 0.45, heightRoadFactor: 0.78, minHeight: 1 },
  guardrail: { kind: "fence", widthToHeight: 0.32, heightRoadFactor: 0.16, minHeight: 1 },
  water_wall: { kind: "rock", widthToHeight: 1.2, heightRoadFactor: 0.22, minHeight: 1 },
  rock_spire: { kind: "rock", widthToHeight: 0.62, heightRoadFactor: 1.33, minHeight: 1 },
  heat_sign: { kind: "sign", widthToHeight: 0.58, heightRoadFactor: 0.67, minHeight: 1 },
};

const DEFAULT_RECOLOUR_IN_FLIGHT = new Set<string>();
const DEFAULT_RECOLOUR_FAILED = new Set<string>();

const FALLBACK_COLORS: RoadColors = {
  skyTop: DEFAULT_COLORS.skyTop,
  skyBottom: DEFAULT_COLORS.skyBottom,
  grassLight: DEFAULT_COLORS.grassLight,
  grassDark: DEFAULT_COLORS.grassDark,
  rumbleLight: DEFAULT_COLORS.rumbleLight,
  rumbleDark: DEFAULT_COLORS.rumbleDark,
  roadLight: DEFAULT_COLORS.roadLight,
  roadDark: DEFAULT_COLORS.roadDark,
  lane: DEFAULT_COLORS.lane,
  finishLight: DEFAULT_COLORS.finishLight,
  finishDark: DEFAULT_COLORS.finishDark,
};

function pickAlternating(index: number, period: number, light: string, dark: string): string {
  return Math.floor(index / period) % 2 === 0 ? light : dark;
}

/** Fill a vertical-axis trapezoid between two centered horizontal segments. */
function drawTrapezoid(
  ctx: CanvasRenderingContext2D,
  color: string,
  nearX: number,
  nearY: number,
  nearHalfW: number,
  farX: number,
  farY: number,
  farHalfW: number,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(nearX - nearHalfW, nearY);
  ctx.lineTo(nearX + nearHalfW, nearY);
  ctx.lineTo(farX + farHalfW, farY);
  ctx.lineTo(farX - farHalfW, farY);
  ctx.closePath();
  ctx.fill();
}

/**
 * Draw the sky band as a vertical gradient covering the full viewport.
 * The road strips are drawn over this base.
 */
function drawSky(ctx: CanvasRenderingContext2D, viewport: Viewport, colors: RoadColors): void {
  const gradient = ctx.createLinearGradient(0, 0, 0, viewport.height);
  gradient.addColorStop(0, colors.skyTop);
  gradient.addColorStop(1, colors.skyBottom);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, viewport.width, viewport.height);
}

/**
 * Render the projected road strips into the given Canvas2D context.
 *
 * The strip array is iterated back-to-front so nearer strips paint over
 * farther ones, producing the classic Outrun look without a depth buffer.
 *
 * Empty strip arrays are valid; the drawer renders only the sky band.
 */
export function drawRoad(
  ctx: CanvasRenderingContext2D,
  strips: readonly Strip[],
  viewport: Viewport,
  options: DrawRoadOptions = {},
): void {
  const colors = options.colors ?? FALLBACK_COLORS;

  if (options.parallax) {
    drawParallax(ctx, options.parallax.layers, options.parallax.camera, viewport);
  } else {
    drawSky(ctx, viewport, colors);
  }

  // VFX runs after the sky / parallax pass so the flash overlay sits on
  // top of the static background but BEHIND the road strips. The shake
  // offset, however, has to wrap the road draw so the road translates
  // as one unit. We capture the offset here, then save / translate /
  // restore around the strip loop.
  const shakeOffset = options.vfx ? drawVfx(ctx, options.vfx, viewport) : null;
  const highContrastSigns =
    options.weatherEffects?.highContrastRoadsideSigns === true;

  if (strips.length >= 2) {
    if (shakeOffset && (shakeOffset.dx !== 0 || shakeOffset.dy !== 0)) {
      ctx.save();
      ctx.translate(shakeOffset.dx, shakeOffset.dy);
      drawStrips(ctx, strips, viewport, colors, markingCameraZFrom(options));
      drawRoadsideSprites(
        ctx,
        strips,
        viewport,
        highContrastSigns,
        options.spriteDensityFactor,
        options.roadsideAtlas,
      );
      drawPickupSprites(ctx, options.pickupSprites);
      ctx.restore();
    } else {
      drawStrips(ctx, strips, viewport, colors, markingCameraZFrom(options));
      drawRoadsideSprites(
        ctx,
        strips,
        viewport,
        highContrastSigns,
        options.spriteDensityFactor,
        options.roadsideAtlas,
      );
      drawPickupSprites(ctx, options.pickupSprites);
    }
  }

  if (options.aiCars && options.aiCars.length > 0) {
    drawAICars(ctx, options.aiCars, viewport);
  }

  // Ghost car paints over the road strips so the player sees their best
  // line, but BEFORE the dust pool so off-road dust the live car kicks
  // up still occludes the ghost rather than the ghost showing through
  // the plume. The shake offset is intentionally not applied to the
  // ghost: a §16 impact shake should not drag the recorded path with
  // the live car. Painted unconditionally on a non-empty `ghostCar`
  // prop so an empty strip array (test / dev scenes that draw the sky
  // but no road) still surfaces the ghost overlay.
  if (options.ghostCar) {
    drawGhostCar(ctx, options.ghostCar, viewport);
  }

  // Dust paints over the road / grass strips and under the live car.
  // The pool is owned by the caller; the drawer is read-only on it.
  if (options.dust) {
    drawDust(ctx, options.dust, viewport);
  }

  if (options.weatherEffects) {
    drawWeatherEffects(ctx, viewport, strips, options.weatherEffects);
  }

  drawHeatShimmer(ctx, viewport, options.heatShimmer);
  drawTunnelAdaptation(ctx, strips, viewport, options.tunnelAdaptation);

  // §16 radial speed lines: paint above the road / weather / tunnel
  // adaptation but BEFORE the player car so streaks read as the
  // world rushing past the camera, not the driver itself.
  if (options.speedLines) {
    drawSpeedLines(ctx, options.speedLines, viewport);
  }

  if (options.playerCar) {
    drawPlayerCar(ctx, options.playerCar, viewport);
  }

  drawPickupFeedback(ctx, viewport, options.pickupFeedback);
}

function drawPickupSprites(
  ctx: CanvasRenderingContext2D,
  sprites: readonly ProjectedPickupSprite[] | null | undefined,
): void {
  if (!sprites || sprites.length === 0) return;
  for (const sprite of sprites) {
    drawPickupSprite(ctx, sprite);
  }
}

function drawPickupSprite(
  ctx: CanvasRenderingContext2D,
  sprite: ProjectedPickupSprite,
): void {
  if (
    !Number.isFinite(sprite.screenX) ||
    !Number.isFinite(sprite.screenY) ||
    !Number.isFinite(sprite.screenW) ||
    sprite.screenW <= 0
  ) {
    return;
  }

  if (sprite.kind === "nitro") {
    drawNitroPickupSprite(ctx, sprite.screenX, sprite.screenY, sprite.screenW);
  } else {
    drawCashPickupSprite(ctx, sprite.screenX, sprite.screenY, sprite.screenW);
  }
}

function drawCashPickupSprite(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  baseY: number,
  size: number,
): void {
  const half = size / 2;
  const y = baseY - size * 0.82;
  const prevFill = ctx.fillStyle;
  try {
    ctx.fillStyle = PICKUP_CASH_SHADOW;
    ctx.beginPath();
    ctx.moveTo(centerX, y + size * 0.12);
    ctx.lineTo(centerX + half, y + half + size * 0.12);
    ctx.lineTo(centerX, y + size + size * 0.12);
    ctx.lineTo(centerX - half, y + half + size * 0.12);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = PICKUP_CASH_FILL;
    ctx.beginPath();
    ctx.moveTo(centerX, y);
    ctx.lineTo(centerX + half, y + half);
    ctx.lineTo(centerX, y + size);
    ctx.lineTo(centerX - half, y + half);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#fff7a8";
    ctx.fillRect(centerX - size * 0.08, y + size * 0.24, size * 0.16, size * 0.52);
    ctx.fillRect(centerX - size * 0.23, y + size * 0.42, size * 0.46, size * 0.14);
  } finally {
    ctx.fillStyle = prevFill;
  }
}

function drawNitroPickupSprite(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  baseY: number,
  size: number,
): void {
  const y = baseY - size * 0.9;
  const prevFill = ctx.fillStyle;
  try {
    ctx.fillStyle = PICKUP_NITRO_SHADOW;
    ctx.fillRect(centerX - size * 0.4, y + size * 0.16, size * 0.8, size * 0.78);
    ctx.fillStyle = PICKUP_NITRO_FILL;
    ctx.fillRect(centerX - size * 0.34, y, size * 0.68, size * 0.78);
    ctx.fillStyle = "#f7fbff";
    ctx.fillRect(centerX - size * 0.22, y + size * 0.12, size * 0.44, size * 0.12);
    ctx.fillStyle = "#2f7dff";
    ctx.fillRect(centerX - size * 0.14, y + size * 0.32, size * 0.28, size * 0.34);
  } finally {
    ctx.fillStyle = prevFill;
  }
}

function drawPickupFeedback(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  feedback: DrawRoadOptions["pickupFeedback"],
): void {
  if (!feedback) return;
  if (viewport.width <= 0 || viewport.height <= 0) return;
  if (!Number.isFinite(feedback.ageMs)) return;
  const t = Math.max(0, Math.min(1, feedback.ageMs / PICKUP_FEEDBACK_TTL_MS));
  if (t >= 1) return;

  const prevFill = ctx.fillStyle;
  const prevAlpha = ctx.globalAlpha;
  try {
    ctx.globalAlpha = 1 - t;
    ctx.fillStyle = feedback.kind === "nitro" ? PICKUP_NITRO_FILL : PICKUP_CASH_FILL;
    const centerX = viewport.width / 2;
    const centerY = viewport.height * 0.78;
    const radius = 22 + t * 32;
    const size = 5;
    for (let i = 0; i < 8; i += 1) {
      const angle = (Math.PI * 2 * i) / 8;
      ctx.fillRect(
        centerX + Math.cos(angle) * radius - size / 2,
        centerY + Math.sin(angle) * radius - size / 2,
        size,
        size,
      );
    }
  } finally {
    ctx.fillStyle = prevFill;
    ctx.globalAlpha = prevAlpha;
  }
}

function drawAICars(
  ctx: CanvasRenderingContext2D,
  cars: NonNullable<DrawRoadOptions["aiCars"]>,
  viewport: Viewport,
): void {
  const ordered = cars
    .filter((car) => isDrawableProjectedCar(car, viewport))
    .slice()
    .sort((a, b) => b.depthMeters - a.depthMeters);
  for (const car of ordered) {
    drawProjectedAICar(ctx, car);
  }
}

function isDrawableProjectedCar(
  car: NonNullable<DrawRoadOptions["aiCars"]>[number],
  viewport: Viewport,
): boolean {
  if (
    !(
      viewport.width > 0 &&
      viewport.height > 0 &&
      Number.isFinite(car.screenX) &&
      Number.isFinite(car.screenY) &&
      Number.isFinite(car.screenW) &&
      car.screenW > 0 &&
      Number.isFinite(car.depthMeters) &&
      car.depthMeters > 0
    )
  ) {
    return false;
  }

  const halfW = car.screenW / 2;
  return (
    car.screenY >= -viewport.height * 0.2 &&
    car.screenY <= viewport.height * 1.15 &&
    car.screenX + halfW >= 0 &&
    car.screenX - halfW <= viewport.width
  );
}

function drawProjectedAICar(
  ctx: CanvasRenderingContext2D,
  car: NonNullable<DrawRoadOptions["aiCars"]>[number],
): void {
  const alpha =
    typeof car.alpha === "number" && Number.isFinite(car.alpha)
      ? Math.max(0, Math.min(1, car.alpha))
      : AI_CAR_DEFAULT_ALPHA;
  if (alpha <= 0) return;

  if (car.atlas?.image) {
    const frames = resolveCarRenderFrames(
      car.atlas,
      selectCarFramePlan({
        frameIndex: car.frameIndex ?? 0,
        braking: car.braking === true,
        nitroActive: car.nitroActive === true,
        speedMetersPerSecond: car.speedMetersPerSecond ?? 0,
        damageTotal: car.damageTotal ?? 0,
        spriteSet: car.spriteSet,
      }),
    );
    if (frames) {
      drawAtlasCarFrame(ctx, car.atlas.image, frames.base, car.screenX, car.screenY, car.screenW, alpha);
      if (frames.trailOverlay) {
        drawAtlasCarFrame(ctx, car.atlas.image, frames.trailOverlay, car.screenX, car.screenY, car.screenW, alpha);
      }
      if (frames.brakeOverlay) {
        drawAtlasCarFrame(ctx, car.atlas.image, frames.brakeOverlay, car.screenX, car.screenY, car.screenW, alpha);
      }
      if (frames.nitroOverlay) {
        drawAtlasCarFrame(ctx, car.atlas.image, frames.nitroOverlay, car.screenX, car.screenY, car.screenW, alpha);
      }
      if (frames.damageOverlay) {
        drawAtlasCarFrame(ctx, car.atlas.image, frames.damageOverlay, car.screenX, car.screenY, car.screenW, alpha);
      }
      return;
    }
  }

  const height = car.screenW * 0.52;
  const halfW = car.screenW / 2;
  const bottomY = car.screenY;
  const topY = bottomY - height;
  const prevAlpha = ctx.globalAlpha;
  const prevFill = ctx.fillStyle;
  try {
    ctx.globalAlpha = alpha;
    ctx.fillStyle = AI_CAR_DEFAULT_SHADOW;
    ctx.fillRect(car.screenX - halfW * 0.72, bottomY - height * 0.08, car.screenW * 0.72, height * 0.16);
    ctx.fillStyle = car.fill ?? AI_CAR_DEFAULT_FILL;
    ctx.beginPath();
    ctx.moveTo(car.screenX, topY);
    ctx.lineTo(car.screenX + halfW * 0.82, bottomY - height * 0.22);
    ctx.lineTo(car.screenX + halfW * 0.62, bottomY);
    ctx.lineTo(car.screenX - halfW * 0.62, bottomY);
    ctx.lineTo(car.screenX - halfW * 0.82, bottomY - height * 0.22);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = PLAYER_CAR_DEFAULT_WINDSHIELD;
    ctx.fillRect(car.screenX - halfW * 0.28, topY + height * 0.35, halfW * 0.56, height * 0.24);
    ctx.fillStyle = PLAYER_CAR_DEFAULT_TAIL_LIGHT;
    ctx.fillRect(car.screenX - halfW * 0.46, bottomY - height * 0.18, halfW * 0.28, height * 0.08);
    ctx.fillRect(car.screenX + halfW * 0.18, bottomY - height * 0.18, halfW * 0.28, height * 0.08);
  } finally {
    ctx.globalAlpha = prevAlpha;
    ctx.fillStyle = prevFill;
  }
}

function markingCameraZFrom(options: DrawRoadOptions): number | null {
  return options.markingCameraZ ?? options.parallax?.camera.z ?? null;
}

function roadsideStyleFor(id: string): RoadsideSpriteStyle | null {
  if (id === "default") return null;
  return ROADSIDE_SPRITE_STYLES[id] ?? null;
}

function shouldDrawRoadsideSprite(strip: Strip, side: "left" | "right"): boolean {
  const offset = side === "left" ? 0 : Math.floor(ROADSIDE_DRAW_PERIOD / 2);
  return (strip.segment.index + offset) % ROADSIDE_DRAW_PERIOD === 0;
}

function shouldKeepSpriteForDensity(strip: Strip, side: "left" | "right", density = 1): boolean {
  const clamped = Math.max(0, Math.min(1, density));
  if (clamped >= 1) return true;
  if (clamped <= 0) return false;
  const drawOffset = side === "left" ? 0 : Math.floor(ROADSIDE_DRAW_PERIOD / 2);
  const opportunityIndex = Math.floor((strip.segment.index + drawOffset) / ROADSIDE_DRAW_PERIOD);
  const keptSlots = Math.max(1, Math.min(4, Math.round(clamped * 4)));
  return opportunityIndex % 4 < keptSlots;
}

function drawRoadsideSprites(
  ctx: CanvasRenderingContext2D,
  strips: readonly Strip[],
  viewport: Viewport,
  highContrastSigns = false,
  spriteDensityFactor = 1,
  roadsideAtlas: DrawRoadOptions["roadsideAtlas"] = null,
): void {
  for (let i = strips.length - 1; i >= 0; i--) {
    const strip = strips[i];
    if (!strip?.visible) continue;
    drawRoadsideSprite(
      ctx,
      strip,
      viewport,
      "left",
      highContrastSigns,
      spriteDensityFactor,
      roadsideAtlas,
    );
    drawRoadsideSprite(
      ctx,
      strip,
      viewport,
      "right",
      highContrastSigns,
      spriteDensityFactor,
      roadsideAtlas,
    );
  }
}

function drawRoadsideSprite(
  ctx: CanvasRenderingContext2D,
  strip: Strip,
  viewport: Viewport,
  side: "left" | "right",
  highContrastSigns: boolean,
  spriteDensityFactor: number,
  roadsideAtlas: DrawRoadOptions["roadsideAtlas"],
): void {
  if (!shouldDrawRoadsideSprite(strip, side)) return;
  if (!shouldKeepSpriteForDensity(strip, side, spriteDensityFactor)) return;
  const id =
    side === "left" ? strip.segment.roadsideLeftId : strip.segment.roadsideRightId;
  const style = roadsideStyleFor(id);
  if (!style) return;
  if (viewport.width <= 0 || viewport.height <= 0) return;
  if (!Number.isFinite(strip.screenW) || strip.screenW <= 0) return;
  if (!Number.isFinite(strip.screenX) || !Number.isFinite(strip.screenY)) return;

  const maxHeight = viewport.height * ROADSIDE_MAX_HEIGHT_FRACTION;
  const height = Math.max(
    style.minHeight,
    Math.min(maxHeight, strip.screenW * style.heightRoadFactor * SPRITE_BASE_SCALE),
  );
  const width = height * style.widthToHeight;
  const sideSign = side === "left" ? -1 : 1;
  // Q-017 recommended default: lift the placement offset from 1.32 to 1.70
  // so props sit roughly 3 to 5 m off the rumble strip instead of 1.5 m off.
  // The pre-fix offset put trees and poles uncomfortably close to the road
  // edge, which read as a road-side wall rather than a roadside scene.
  const baseX = strip.screenX + sideSign * strip.screenW * 1.7;
  const baseY = strip.screenY;

  if (baseY < 0 || baseY - height > viewport.height) return;
  if (baseX + width < 0 || baseX - width > viewport.width) return;

  if (
    roadsideAtlas?.atlas.image &&
    drawRoadsideAtlasSprite(
      ctx,
      roadsideAtlas,
      id,
      strip.segment.index,
      baseX,
      baseY,
      width,
      height,
    )
  ) {
    return;
  }

  switch (style.kind) {
    case "tree":
      drawTreeSprite(ctx, baseX, baseY, width, height);
      break;
    case "sign":
      drawSignSprite(ctx, baseX, baseY, width, height, highContrastSigns);
      break;
    case "fence":
      drawFenceSprite(ctx, baseX, baseY, width, height);
      break;
    case "rock":
      drawRockSprite(ctx, baseX, baseY, width, height);
      break;
    case "pole":
      drawPoleSprite(ctx, baseX, baseY, width, height);
      break;
  }
}

function drawRoadsideAtlasSprite(
  ctx: CanvasRenderingContext2D,
  roadsideAtlas: NonNullable<DrawRoadOptions["roadsideAtlas"]>,
  spriteId: string,
  frameIndex: number,
  baseX: number,
  baseY: number,
  width: number,
  height: number,
): boolean {
  const image = roadsideAtlas.atlas.image;
  if (!image) return false;

  let keyFrameIndex: number;
  let sprite: ReturnType<typeof frame>;
  try {
    const count = spriteFrameCount(roadsideAtlas.atlas, spriteId);
    keyFrameIndex = ((frameIndex % count) + count) % count;
    sprite = frame(roadsideAtlas.atlas, spriteId, keyFrameIndex);
  } catch {
    return false;
  }

  const anchor = sprite.anchor ?? { x: 0.5, y: 1 };
  const dx = baseX - width * anchor.x;
  const dy = baseY - height * anchor.y;

  if (roadsideAtlas.palette && spriteCanRecolour(roadsideAtlas.atlas, spriteId)) {
    const key = paletteCacheKey(spriteId, keyFrameIndex, roadsideAtlas.palette.id);
    const cached = roadsideAtlas.cache?.get(key);
    if (cached) {
      ctx.drawImage(cached, dx, dy, width, height);
      return true;
    }
    if (roadsideAtlas.cache) {
      const inFlight = roadsideAtlas.inFlight ?? DEFAULT_RECOLOUR_IN_FLIGHT;
      const failed = roadsideAtlas.failed ?? DEFAULT_RECOLOUR_FAILED;
      if (!inFlight.has(key) && !failed.has(key)) {
        inFlight.add(key);
        void recolourFrameToImageBitmap(image, sprite, roadsideAtlas.palette)
          .then((recoloured) => {
            roadsideAtlas.cache?.set(key, recoloured);
          })
          .catch((error: unknown) => {
            failed.add(key);
            roadsideAtlas.onRecolourError?.(key, error);
          })
          .finally(() => {
            inFlight.delete(key);
          });
      }
    }
  }

  ctx.drawImage(image, sprite.x, sprite.y, sprite.w, sprite.h, dx, dy, width, height);
  return true;
}

function drawTreeSprite(
  ctx: CanvasRenderingContext2D,
  baseX: number,
  baseY: number,
  width: number,
  height: number,
): void {
  ctx.fillStyle = "#1b3a20";
  ctx.fillRect(baseX - width * 0.08, baseY - height * 0.42, width * 0.16, height * 0.42);
  ctx.fillStyle = "#245c2f";
  ctx.beginPath();
  ctx.moveTo(baseX, baseY - height);
  ctx.lineTo(baseX + width * 0.55, baseY - height * 0.32);
  ctx.lineTo(baseX - width * 0.55, baseY - height * 0.32);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#2f7a3a";
  ctx.beginPath();
  ctx.moveTo(baseX, baseY - height * 0.8);
  ctx.lineTo(baseX + width * 0.45, baseY - height * 0.18);
  ctx.lineTo(baseX - width * 0.45, baseY - height * 0.18);
  ctx.closePath();
  ctx.fill();
}

function drawSignSprite(
  ctx: CanvasRenderingContext2D,
  baseX: number,
  baseY: number,
  width: number,
  height: number,
  highContrast = false,
): void {
  ctx.fillStyle = "#d9d7c7";
  ctx.fillRect(baseX - width * 0.08, baseY - height * 0.58, width * 0.16, height * 0.58);
  ctx.fillStyle = highContrast ? "#fff36a" : "#e7d24d";
  ctx.fillRect(baseX - width * 0.5, baseY - height, width, height * 0.38);
  ctx.fillStyle = highContrast ? "#05070d" : "#23304d";
  ctx.fillRect(baseX - width * 0.36, baseY - height * 0.88, width * 0.72, height * 0.08);
  if (highContrast) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(baseX - width * 0.48, baseY - height * 0.99, width * 0.96, height * 0.04);
    ctx.fillRect(baseX - width * 0.48, baseY - height * 0.66, width * 0.96, height * 0.04);
  }
}

function drawFenceSprite(
  ctx: CanvasRenderingContext2D,
  baseX: number,
  baseY: number,
  width: number,
  height: number,
): void {
  ctx.fillStyle = "#d7d4c5";
  ctx.fillRect(baseX - width * 0.5, baseY - height * 0.7, width, height * 0.12);
  ctx.fillRect(baseX - width * 0.5, baseY - height * 0.38, width, height * 0.12);
  ctx.fillStyle = "#8d8a80";
  ctx.fillRect(baseX - width * 0.08, baseY - height, width * 0.16, height);
}

function drawRockSprite(
  ctx: CanvasRenderingContext2D,
  baseX: number,
  baseY: number,
  width: number,
  height: number,
): void {
  ctx.fillStyle = "#767c82";
  ctx.beginPath();
  ctx.moveTo(baseX - width * 0.5, baseY);
  ctx.lineTo(baseX - width * 0.36, baseY - height * 0.7);
  ctx.lineTo(baseX + width * 0.08, baseY - height);
  ctx.lineTo(baseX + width * 0.5, baseY - height * 0.42);
  ctx.lineTo(baseX + width * 0.44, baseY);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#9aa0a5";
  ctx.fillRect(baseX - width * 0.18, baseY - height * 0.7, width * 0.28, height * 0.12);
}

function drawPoleSprite(
  ctx: CanvasRenderingContext2D,
  baseX: number,
  baseY: number,
  width: number,
  height: number,
): void {
  ctx.fillStyle = "#c9ccd2";
  ctx.fillRect(baseX - width * 0.22, baseY - height, width * 0.44, height);
  ctx.fillStyle = "#f1e36a";
  ctx.fillRect(baseX - width * 1.4, baseY - height, width * 2.8, height * 0.09);
}

function drawWeatherEffects(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  strips: readonly Strip[],
  effects: NonNullable<DrawRoadOptions["weatherEffects"]>,
): void {
  if (viewport.width <= 0 || viewport.height <= 0) return;
  const settings = resolveWeatherEffectSettings(effects);

  switch (effects.weather) {
    case "light_rain":
    case "rain":
    case "heavy_rain":
      drawRainEffects(ctx, viewport, strips, effects.weather, settings.particleIntensity);
      return;
    case "snow":
      drawSnowEffects(ctx, viewport, strips, settings.particleIntensity);
      return;
    case "fog":
      drawFogFade(ctx, viewport, strips, settings.alphaScale, settings.fogFloorClamp);
      return;
    case "dusk":
    case "night":
      drawNightBloom(ctx, viewport, effects.weather, settings.bloomScale);
      return;
    case "clear":
    case "overcast":
      return;
  }
}

function drawHeatShimmer(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  options: DrawRoadOptions["heatShimmer"],
): void {
  if (options?.enabled !== true) return;
  if (viewport.width <= 0 || viewport.height <= 0) return;
  const intensity = clampUnit(options.intensity ?? 1);
  if (intensity <= 0) return;
  const phaseMeters =
    typeof options.phaseMeters === "number" && Number.isFinite(options.phaseMeters)
      ? options.phaseMeters
      : 0;

  const prevFill = ctx.fillStyle;
  const prevAlpha = ctx.globalAlpha;
  try {
    ctx.fillStyle = HEAT_SHIMMER_FILL;
    const segmentWidth = Math.max(24, viewport.width * 0.18);
    const gap = segmentWidth * 0.92;
    for (let i = 0; i < HEAT_SHIMMER_BAND_COUNT; i += 1) {
      const y = viewport.height * (0.24 + i * 0.033);
      const h = Math.max(1, viewport.height * (0.004 + i * 0.0007));
      const drift =
        (((phaseMeters * 0.22 + i * 37) % (segmentWidth + gap)) + segmentWidth + gap) %
        (segmentWidth + gap);
      ctx.globalAlpha = HEAT_SHIMMER_MAX_ALPHA * intensity * (1 - i * 0.08);
      for (let x = -segmentWidth + drift; x < viewport.width; x += segmentWidth + gap) {
        ctx.fillRect(x, y, segmentWidth, h);
      }
    }
  } finally {
    ctx.fillStyle = prevFill;
    ctx.globalAlpha = prevAlpha;
  }
}

interface ResolvedWeatherEffectSettings {
  particleIntensity: number;
  alphaScale: number;
  fogFloorClamp: number;
  bloomScale: number;
}

function resolveWeatherEffectSettings(
  effects: NonNullable<DrawRoadOptions["weatherEffects"]>,
): ResolvedWeatherEffectSettings {
  const reductionScale =
    effects.visualReduction === true ? WEATHER_EFFECT_REDUCTION_SCALE : 1;
  const particleIntensity = clampUnit(effects.particleIntensity ?? 1) * reductionScale;
  const alphaScale = reductionScale;
  const glareScale = effects.reducedGlare === true ? 0.55 : 1;
  const flashScale = effects.flashReduction === true ? 0.45 : 1;
  return {
    particleIntensity,
    alphaScale,
    fogFloorClamp: clampUnit(effects.fogFloorClamp ?? 0),
    bloomScale: reductionScale * glareScale * flashScale,
  };
}

function drawRainEffects(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  strips: readonly Strip[],
  weather: Extract<WeatherOption, "light_rain" | "rain" | "heavy_rain">,
  intensity: number,
): void {
  if (intensity <= 0) return;
  drawRainRoadSheen(ctx, viewport, weather, intensity);
  drawRainStreaks(ctx, viewport, strips, weather, intensity);
}

function drawRainRoadSheen(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  weather: Extract<WeatherOption, "light_rain" | "rain" | "heavy_rain">,
  intensity: number,
): void {
  const weatherScale = weather === "heavy_rain" ? 1 : weather === "rain" ? 0.72 : 0.45;
  const alpha = RAIN_ROAD_SHEEN_MAX_ALPHA * weatherScale * intensity;
  if (alpha <= 0) return;

  const prevFill = ctx.fillStyle;
  const prevAlpha = ctx.globalAlpha;
  try {
    ctx.fillStyle = RAIN_ROAD_SHEEN_FILL;
    ctx.globalAlpha = alpha;
    ctx.fillRect(
      viewport.width * 0.08,
      viewport.height * 0.62,
      viewport.width * 0.84,
      viewport.height * 0.075,
    );
    ctx.globalAlpha = alpha * 0.55;
    ctx.fillRect(
      viewport.width * 0.18,
      viewport.height * 0.78,
      viewport.width * 0.64,
      viewport.height * 0.045,
    );
  } finally {
    ctx.fillStyle = prevFill;
    ctx.globalAlpha = prevAlpha;
  }
}

function drawRainStreaks(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  strips: readonly Strip[],
  weather: Extract<WeatherOption, "light_rain" | "rain" | "heavy_rain">,
  intensity: number,
): void {
  const bands = weatherProjectionBands(strips, viewport);
  const baseCount = weather === "heavy_rain" ? 92 : weather === "rain" ? 58 : 32;
  const count = Math.round(baseCount * intensity);
  if (count <= 0 || bands.length === 0) return;
  const alpha = (weather === "heavy_rain" ? 0.34 : weather === "rain" ? 0.26 : 0.18) * intensity;
  const prevFill = ctx.fillStyle;
  const prevAlpha = ctx.globalAlpha;
  try {
    ctx.globalAlpha = alpha;
    ctx.fillStyle = "#cfefff";
    for (let i = 0; i < count; i++) {
      const band = bands[i % bands.length]!;
      const depthSeed = hashUnit(i * 131 + band.index * 17);
      const widthSeed = hashUnit(i * 199 + band.index * 29);
      const y = band.topY + depthSeed * band.height;
      const depth = band.depthAt(y);
      const x = band.centerAt(depth) + (widthSeed * 2 - 1) * band.halfWidthAt(depth) * 1.4;
      const scale = Math.max(0.45, Math.min(1.45, band.scaleAt(depth) * 1.1));
      const h = (weather === "heavy_rain" ? 18 : 13) * scale;
      const w = Math.max(1, Math.round(1 + scale));
      ctx.fillRect(x, y, w, h);
    }
  } finally {
    ctx.fillStyle = prevFill;
    ctx.globalAlpha = prevAlpha;
  }
}

function drawSnowParticles(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  strips: readonly Strip[],
  intensity: number,
): void {
  const bands = weatherProjectionBands(strips, viewport);
  const count = Math.round(54 * intensity);
  if (count <= 0 || bands.length === 0) return;
  const prevFill = ctx.fillStyle;
  const prevAlpha = ctx.globalAlpha;
  try {
    ctx.globalAlpha = 0.72 * intensity;
    ctx.fillStyle = "#f4fbff";
    for (let i = 0; i < count; i++) {
      const band = bands[i % bands.length]!;
      const depthSeed = hashUnit(i * 149 + band.index * 23);
      const widthSeed = hashUnit(i * 181 + band.index * 31);
      const y = band.topY + depthSeed * band.height;
      const depth = band.depthAt(y);
      const size = Math.max(1, Math.round((i % 5 === 0 ? 3 : 2) * band.scaleAt(depth)));
      const x = band.centerAt(depth) + (widthSeed * 2 - 1) * band.halfWidthAt(depth) * 1.55;
      ctx.fillRect(x, y, size, size);
    }
  } finally {
    ctx.fillStyle = prevFill;
    ctx.globalAlpha = prevAlpha;
  }
}

function drawSnowEffects(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  strips: readonly Strip[],
  intensity: number,
): void {
  if (intensity <= 0) return;
  drawSnowRoadsideWhitening(ctx, viewport, intensity);
  drawSnowParticles(ctx, viewport, strips, intensity);
}

function drawSnowRoadsideWhitening(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  intensity: number,
): void {
  const alpha = SNOW_ROADSIDE_WHITENING_MAX_ALPHA * intensity;
  if (alpha <= 0) return;

  const prevFill = ctx.fillStyle;
  const prevAlpha = ctx.globalAlpha;
  try {
    ctx.fillStyle = SNOW_ROADSIDE_WHITENING_FILL;
    ctx.globalAlpha = alpha * 0.65;
    ctx.fillRect(0, viewport.height * 0.42, viewport.width, viewport.height * 0.08);
    ctx.globalAlpha = alpha;
    ctx.fillRect(0, viewport.height * 0.62, viewport.width * 0.2, viewport.height * 0.28);
    ctx.fillRect(
      viewport.width * 0.8,
      viewport.height * 0.62,
      viewport.width * 0.2,
      viewport.height * 0.28,
    );
  } finally {
    ctx.fillStyle = prevFill;
    ctx.globalAlpha = prevAlpha;
  }
}

function drawFogFade(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  strips: readonly Strip[],
  intensity: number,
  fogFloorClamp: number,
): void {
  const visibility = Math.max(visibilityForWeather("fog"), fogFloorClamp);
  const bands = weatherProjectionBands(strips, viewport);
  const prevFill = ctx.fillStyle;
  const prevAlpha = ctx.globalAlpha;
  try {
    ctx.fillStyle = "#cbd7e1";
    for (let i = bands.length - 1; i >= 0; i--) {
      const band = bands[i]!;
      const distance = bands.length <= 1 ? 1 : i / (bands.length - 1);
      const alpha = Math.min(0.24, (1 - visibility) * intensity * (0.1 + distance * 0.28));
      if (alpha <= 0) continue;
      ctx.globalAlpha = alpha;
      ctx.fillRect(0, band.topY, viewport.width, band.height);
    }
    ctx.globalAlpha = Math.min(0.5, (1 - visibility) * 0.72 * intensity);
    ctx.fillRect(0, 0, viewport.width, viewport.height * 0.72);
    ctx.globalAlpha = Math.min(0.28, (1 - visibility) * 0.4 * intensity);
    ctx.fillRect(0, viewport.height * 0.48, viewport.width, viewport.height * 0.34);
  } finally {
    ctx.fillStyle = prevFill;
    ctx.globalAlpha = prevAlpha;
  }
}

interface WeatherProjectionBand {
  index: number;
  topY: number;
  height: number;
  depthAt(y: number): number;
  centerAt(depth: number): number;
  halfWidthAt(depth: number): number;
  scaleAt(depth: number): number;
}

interface WeatherProjectionEdge {
  screenX: number;
  screenY: number;
  screenW: number;
  scale: number;
}

function weatherProjectionBands(
  strips: readonly Strip[],
  viewport: Viewport,
): readonly WeatherProjectionBand[] {
  const minScreenY = -viewport.height * 0.1;
  const maxScreenY = viewport.height * 1.1;
  const bands: WeatherProjectionBand[] = [];
  let previousEdge: WeatherProjectionEdge | null = null;
  let index = 0;

  for (const edge of weatherProjectionEdges(strips)) {
    if (edge.screenY < minScreenY || edge.screenY > maxScreenY) continue;
    if (previousEdge === null) {
      previousEdge = edge;
      continue;
    }

    const near = previousEdge;
    const far = edge;
    const topY = Math.max(0, Math.min(near.screenY, far.screenY));
    const bottomY = Math.min(viewport.height, Math.max(near.screenY, far.screenY));
    const height = bottomY - topY;
    if (height >= 1) {
      bands.push({
        index,
        topY,
        height,
        depthAt: (y) => clampUnit((near.screenY - y) / Math.max(1, near.screenY - far.screenY)),
        centerAt: (depth) => lerpNumber(near.screenX, far.screenX, clampUnit(depth)),
        halfWidthAt: (depth) => lerpNumber(near.screenW, far.screenW, clampUnit(depth)),
        scaleAt: (depth) => lerpNumber(near.scale, far.scale, clampUnit(depth)),
      });
      index += 1;
    }

    previousEdge = edge;
  }
  return bands;
}

function weatherProjectionEdges(strips: readonly Strip[]): readonly WeatherProjectionEdge[] {
  const edges: WeatherProjectionEdge[] = [];
  for (const strip of strips) {
    if (!strip.visible) continue;
    if (!isFiniteProjection(strip.screenX, strip.screenY, strip.screenW, strip.scale)) continue;
    if (strip.foreground && isFiniteProjection(
      strip.foreground.screenX,
      strip.foreground.screenY,
      strip.foreground.screenW,
      strip.scale,
    )) {
      edges.push({
        screenX: strip.foreground.screenX,
        screenY: strip.foreground.screenY,
        screenW: strip.foreground.screenW,
        scale: Math.max(strip.scale, 1),
      });
    }
    edges.push({
      screenX: strip.screenX,
      screenY: strip.screenY,
      screenW: strip.screenW,
      scale: strip.scale,
    });
  }
  return edges;
}

function isFiniteProjection(
  screenX: number,
  screenY: number,
  screenW: number,
  scale: number,
): boolean {
  return (
    Number.isFinite(screenX) &&
    Number.isFinite(screenY) &&
    typeof screenW === "number" &&
    typeof scale === "number" &&
    Number.isFinite(screenW) &&
    Number.isFinite(scale) &&
    screenW > 0 &&
    scale > 0
  );
}

function hashUnit(seed: number): number {
  if (!Number.isFinite(seed)) return 0;

  let x = Math.trunc(seed) | 0;
  x = Math.imul(x ^ (x >>> 16), 0x21f0aaad);
  x = Math.imul(x ^ (x >>> 15), 0x735a2d97);
  x ^= x >>> 15;
  return (x >>> 0) / 0x100000000;
}

function drawNightBloom(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  weather: Extract<WeatherOption, "dusk" | "night">,
  intensity: number,
): void {
  const prevFill = ctx.fillStyle;
  const prevAlpha = ctx.globalAlpha;
  try {
    ctx.globalAlpha = (weather === "night" ? 0.34 : 0.22) * intensity;
    ctx.fillStyle = "#f4e779";
    const y = viewport.height * 0.22;
    const w = viewport.width * 0.12;
    const h = viewport.height * 0.035;
    ctx.fillRect(viewport.width * 0.28 - w / 2, y, w, h);
    ctx.fillRect(viewport.width * 0.72 - w / 2, y, w, h);
  } finally {
    ctx.fillStyle = prevFill;
    ctx.globalAlpha = prevAlpha;
  }
}

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Paint the F-022 ghost car overlay as a translucent placeholder rect.
 *
 * The atlas-frame variant (sampling the player car sprite at the same
 * facing the live car uses, optionally desaturated) lands in the F-022
 * consumer slice that wires `LoadedAtlas` through the renderer. Until
 * then the rect is the contract: a `screenW`-wide, half-square-tall
 * box anchored at the projected ground point, painted at 0.5 alpha by
 * default. The aspect choice (1:0.5) keeps the placeholder visually
 * read as a car silhouette without claiming sprite-grade detail.
 *
 * No-op when the projection collapses to zero width (the ghost is past
 * the draw distance / behind the camera) or when the viewport is
 * zero-area, so callers do not need to gate the call themselves.
 *
 * The `globalAlpha` mutation is wrapped in a `try / finally` save and
 * restore equivalent so a thrown rect (vanishingly unlikely on Canvas2D
 * but possible if the host injects a custom context) cannot leak alpha
 * into the next draw call.
 */
function drawGhostCar(
  ctx: CanvasRenderingContext2D,
  ghost: NonNullable<DrawRoadOptions["ghostCar"]>,
  viewport: Viewport,
): void {
  if (viewport.width <= 0 || viewport.height <= 0) return;
  if (!Number.isFinite(ghost.screenW) || ghost.screenW <= 0) return;
  if (!Number.isFinite(ghost.screenX) || !Number.isFinite(ghost.screenY)) return;

  const alpha =
    typeof ghost.alpha === "number" && Number.isFinite(ghost.alpha)
      ? Math.max(0, Math.min(1, ghost.alpha))
      : GHOST_CAR_DEFAULT_ALPHA;
  if (alpha <= 0) return;

  const fill = ghost.fill ?? GHOST_CAR_DEFAULT_FILL;
  if (ghost.atlas?.image) {
    const sprite = frame(
      ghost.atlas,
      ghost.spriteId ?? PLAYER_CAR_DEFAULT_SPRITE_ID,
      ghost.frameIndex ?? 0,
    );
    drawAtlasCarFrame(
      ctx,
      ghost.atlas.image,
      sprite,
      ghost.screenX,
      ghost.screenY,
      ghost.screenW,
      alpha,
    );
    return;
  }

  // 1:0.5 aspect places the rectangle centered on the projected ground
  // point with the silhouette sitting just above it; matches the §17
  // car-silhouette aspect well enough to read as a fallback vehicle.
  const halfW = ghost.screenW / 2;
  const heightPx = ghost.screenW / 2;

  const prevAlpha = ctx.globalAlpha;
  const prevFill = ctx.fillStyle;
  try {
    ctx.globalAlpha = alpha;
    ctx.fillStyle = fill;
    ctx.fillRect(ghost.screenX - halfW, ghost.screenY - heightPx, ghost.screenW, heightPx);
  } finally {
    ctx.globalAlpha = prevAlpha;
    ctx.fillStyle = prevFill;
  }
}

/**
 * Paint the live player car as a centered chase-camera overlay.
 *
 * The shape is a temporary original silhouette, not a substitute for the
 * §17 directional sprite atlas. It gives the runtime view the correct
 * readable player-car anchor at race start and keeps the visual footprint
 * inside the §16 16 to 22 percent height band.
 */
function drawPlayerCar(
  ctx: CanvasRenderingContext2D,
  car: NonNullable<DrawRoadOptions["playerCar"]>,
  viewport: Viewport,
): void {
  if (viewport.width <= 0 || viewport.height <= 0) return;

  const height = viewport.height * PLAYER_CAR_HEIGHT_FRACTION;
  if (!Number.isFinite(height) || height <= 0) return;
  const width = height * PLAYER_CAR_WIDTH_TO_HEIGHT;
  const centerX = viewport.width / 2;
  const bottomY = viewport.height - Math.max(14, viewport.height * 0.035);
  const topY = bottomY - height;
  const halfW = width / 2;

  if (car.atlas?.image) {
    const frameIndex = car.frameIndex ?? 0;
    const frames = resolveCarRenderFrames(
      car.atlas,
      selectCarFramePlan({
        frameIndex,
        braking: car.braking === true,
        nitroActive: car.nitroActive === true,
        weather: car.weather,
        speedMetersPerSecond: car.speedMetersPerSecond ?? 0,
        damageTotal: car.damageTotal ?? 0,
        spriteSet: car.spriteSet,
      }),
    );
    if (frames) {
      drawAtlasCarFrame(ctx, car.atlas.image, frames.base, centerX, bottomY, width, 1);
      if (frames.trailOverlay) {
        drawAtlasCarFrame(ctx, car.atlas.image, frames.trailOverlay, centerX, bottomY, width, 1);
      }
      if (frames.brakeOverlay) {
        drawAtlasCarFrame(ctx, car.atlas.image, frames.brakeOverlay, centerX, bottomY, width, 1);
      }
      if (frames.nitroOverlay) {
        drawAtlasCarFrame(ctx, car.atlas.image, frames.nitroOverlay, centerX, bottomY, width, 1);
      }
      if (frames.damageOverlay) {
        drawAtlasCarFrame(ctx, car.atlas.image, frames.damageOverlay, centerX, bottomY, width, 1);
      }
      return;
    }
  }

  drawCarWeatherTrail(ctx, centerX, bottomY, width, height, car.weather);

  const prevFill = ctx.fillStyle;
  try {
    ctx.fillStyle = car.tire ?? PLAYER_CAR_DEFAULT_TIRE;
    ctx.beginPath();
    ctx.moveTo(centerX - halfW * 0.68, bottomY - height * 0.12);
    ctx.lineTo(centerX - halfW * 0.54, bottomY - height * 0.12);
    ctx.lineTo(centerX - halfW * 0.48, topY + height * 0.42);
    ctx.lineTo(centerX - halfW * 0.58, topY + height * 0.42);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(centerX + halfW * 0.54, bottomY - height * 0.12);
    ctx.lineTo(centerX + halfW * 0.68, bottomY - height * 0.12);
    ctx.lineTo(centerX + halfW * 0.58, topY + height * 0.42);
    ctx.lineTo(centerX + halfW * 0.48, topY + height * 0.42);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = car.shadow ?? PLAYER_CAR_DEFAULT_SHADOW;
    ctx.beginPath();
    ctx.moveTo(centerX - halfW * 0.82, bottomY);
    ctx.lineTo(centerX + halfW * 0.82, bottomY);
    ctx.lineTo(centerX + halfW * 0.56, topY + height * 0.12);
    ctx.lineTo(centerX - halfW * 0.56, topY + height * 0.12);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = car.fill ?? PLAYER_CAR_DEFAULT_FILL;
    ctx.beginPath();
    ctx.moveTo(centerX - halfW * 0.68, bottomY - height * 0.06);
    ctx.lineTo(centerX + halfW * 0.68, bottomY - height * 0.06);
    ctx.lineTo(centerX + halfW * 0.46, topY + height * 0.04);
    ctx.lineTo(centerX + halfW * 0.24, topY);
    ctx.lineTo(centerX - halfW * 0.24, topY);
    ctx.lineTo(centerX - halfW * 0.46, topY + height * 0.04);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = car.windshield ?? PLAYER_CAR_DEFAULT_WINDSHIELD;
    ctx.beginPath();
    ctx.moveTo(centerX - halfW * 0.28, topY + height * 0.2);
    ctx.lineTo(centerX + halfW * 0.28, topY + height * 0.2);
    ctx.lineTo(centerX + halfW * 0.18, topY + height * 0.48);
    ctx.lineTo(centerX - halfW * 0.18, topY + height * 0.48);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = "#d7a91e";
    ctx.fillRect(centerX - halfW * 0.32, topY + height * 0.56, width * 0.64, height * 0.07);

    ctx.fillStyle = car.tailLight ?? PLAYER_CAR_DEFAULT_TAIL_LIGHT;
    ctx.fillRect(centerX - halfW * 0.52, bottomY - height * 0.24, width * 0.16, height * 0.08);
    ctx.fillRect(centerX + halfW * 0.36, bottomY - height * 0.24, width * 0.16, height * 0.08);
  } finally {
    ctx.fillStyle = prevFill;
  }
}

function drawCarWeatherTrail(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  bottomY: number,
  width: number,
  height: number,
  weather: WeatherOption | undefined,
): void {
  if (
    !weather ||
    weather === "clear" ||
    weather === "overcast" ||
    weather === "dusk" ||
    weather === "night"
  ) {
    return;
  }

  const prevFill = ctx.fillStyle;
  const prevAlpha = ctx.globalAlpha;
  try {
    if (weather === "snow") {
      ctx.globalAlpha = 0.62;
      ctx.fillStyle = "#edf7ff";
      ctx.beginPath();
      ctx.moveTo(centerX - width * 0.36, bottomY - height * 0.12);
      ctx.lineTo(centerX + width * 0.36, bottomY - height * 0.12);
      ctx.lineTo(centerX + width * 0.52, bottomY + height * 0.06);
      ctx.lineTo(centerX - width * 0.52, bottomY + height * 0.06);
      ctx.closePath();
      ctx.fill();
      return;
    }

    if (weather !== "light_rain" && weather !== "rain" && weather !== "heavy_rain") {
      return;
    }

    ctx.globalAlpha = weather === "heavy_rain" ? 0.7 : 0.52;
    ctx.fillStyle = "#d8f4ff";
    ctx.beginPath();
    ctx.moveTo(centerX - width * 0.56, bottomY - height * 0.08);
    ctx.lineTo(centerX - width * 0.18, bottomY - height * 0.02);
    ctx.lineTo(centerX - width * 0.62, bottomY + height * 0.14);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(centerX + width * 0.56, bottomY - height * 0.08);
    ctx.lineTo(centerX + width * 0.18, bottomY - height * 0.02);
    ctx.lineTo(centerX + width * 0.62, bottomY + height * 0.14);
    ctx.closePath();
    ctx.fill();
  } finally {
    ctx.fillStyle = prevFill;
    ctx.globalAlpha = prevAlpha;
  }
}

function drawAtlasCarFrame(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  sprite: ReturnType<typeof frame>,
  anchorX: number,
  anchorY: number,
  width: number,
  alpha: number,
): void {
  if (!Number.isFinite(width) || width <= 0) return;
  if (!Number.isFinite(anchorX) || !Number.isFinite(anchorY)) return;

  const height = width * (sprite.h / sprite.w);
  const pivotX = sprite.anchor?.x ?? 0.5;
  const pivotY = sprite.anchor?.y ?? 1;
  const dx = anchorX - width * pivotX;
  const dy = anchorY - height * pivotY;

  const prevAlpha = ctx.globalAlpha;
  try {
    ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
    ctx.drawImage(image, sprite.x, sprite.y, sprite.w, sprite.h, dx, dy, width, height);
  } finally {
    ctx.globalAlpha = prevAlpha;
  }
}

function drawStrips(
  ctx: CanvasRenderingContext2D,
  strips: readonly Strip[],
  viewport: Viewport,
  colors: RoadColors,
  markingCameraZ: number | null,
): void {
  // Walk far to near so the painter's algorithm covers distant strips with
  // closer ones. We need pairs (near, far); start from the last visible
  // strip and step toward index 0.
  for (let n = strips.length - 1; n >= 1; n--) {
    const far = strips[n];
    const near = strips[n - 1];
    if (!far || !near) continue;
    if (!far.visible || !near.visible) continue;
    drawStripPair(ctx, edgeFromStrip(near), edgeFromStrip(far), viewport, colors);
  }

  const foregroundFar = strips.find((strip) => strip.visible && strip.foreground);
  if (foregroundFar?.foreground) {
    const farEdge = edgeFromStrip(foregroundFar);
    drawStripPair(
      ctx,
      {
        segment: foregroundFar.segment,
        screenX: foregroundFar.foreground.screenX,
        screenY: foregroundFar.foreground.screenY,
        screenW: foregroundFar.foreground.screenW,
        worldZ: markingCameraZ ?? farEdge.worldZ - SEGMENT_LENGTH,
      },
      farEdge,
      viewport,
      colors,
    );
  }
}

interface StripEdge {
  segment: Strip["segment"];
  screenX: number;
  screenY: number;
  screenW: number;
  worldZ: number;
}

function edgeFromStrip(strip: Strip): StripEdge {
  return {
    segment: strip.segment,
    screenX: strip.screenX,
    screenY: strip.screenY,
    screenW: strip.screenW,
    worldZ: strip.segment.worldZ,
  };
}

function drawStripPair(
  ctx: CanvasRenderingContext2D,
  near: StripEdge,
  far: StripEdge,
  viewport: Viewport,
  colors: RoadColors,
): void {
  const segIndex = far.segment.index;
  const worldNear = near.worldZ;
  const worldFar = far.worldZ > worldNear ? far.worldZ : worldNear + SEGMENT_LENGTH;

  const grassColor = pickAlternating(
    segIndex,
    GRASS_STRIPE_LEN,
    colors.grassLight,
    colors.grassDark,
  );
  ctx.fillStyle = grassColor;
  const yTop = far.screenY;
  const yBottom = near.screenY;
  if (yBottom > yTop) {
    ctx.fillRect(0, yTop, viewport.width, yBottom - yTop);
  }

  drawPhasedTrapezoids(
    ctx,
    near,
    far,
    worldNear,
    worldFar,
    1.15,
    RUMBLE_STRIPE_LEN * SEGMENT_LENGTH,
    (worldZ) =>
      pickAlternatingByWorld(worldZ, RUMBLE_STRIPE_LEN * SEGMENT_LENGTH, colors.rumbleLight, colors.rumbleDark),
  );

  drawPhasedTrapezoids(
    ctx,
    near,
    far,
    worldNear,
    worldFar,
    1,
    RUMBLE_STRIPE_LEN * SEGMENT_LENGTH,
    (worldZ) =>
      pickAlternatingByWorld(worldZ, RUMBLE_STRIPE_LEN * SEGMENT_LENGTH, colors.roadLight, colors.roadDark),
  );

  drawDutyCycleTrapezoids(
    ctx,
    near,
    far,
    worldNear,
    worldFar,
    0.03,
    LANE_DASH_CYCLE_METERS,
    LANE_DASH_VISIBLE_METERS,
    (worldZ) =>
      modulo(worldZ, LANE_DASH_CYCLE_METERS) < LANE_DASH_VISIBLE_METERS
        ? colors.lane
        : null,
    { minNearHalfW: 1, minFarHalfW: 0.5 },
  );

  // Start/finish line: the track compiler validates that the "start"
  // checkpoint is at compiled segment 0, so the first FINISH_LINE_SEGMENTS
  // strips of every lap render as a checkerboard band. Skip the strip
  // sitting under the camera — its trapezoid covers the whole foreground
  // and would dominate the view.
  if (
    far.segment.index < FINISH_LINE_SEGMENTS &&
    near.screenY < viewport.height * FINISH_LINE_NEAR_PLANE_GATE
  ) {
    drawFinishLineRow(ctx, near, far, colors, far.segment.index);
  }
}

const FINISH_LINE_NEAR_PLANE_GATE = 0.85;

const FINISH_LINE_SEGMENTS = 2;
const FINISH_LINE_CELLS = 8;

function drawFinishLineRow(
  ctx: CanvasRenderingContext2D,
  near: StripEdge,
  far: StripEdge,
  colors: RoadColors,
  rowIndex: number,
): void {
  for (let i = 0; i < FINISH_LINE_CELLS; i++) {
    const t0 = i / FINISH_LINE_CELLS;
    const t1 = (i + 1) / FINISH_LINE_CELLS;
    const farLeft = far.screenX + (2 * t0 - 1) * far.screenW;
    const farRight = far.screenX + (2 * t1 - 1) * far.screenW;
    const nearLeft = near.screenX + (2 * t0 - 1) * near.screenW;
    const nearRight = near.screenX + (2 * t1 - 1) * near.screenW;
    const isLight = (i + rowIndex) % 2 === 0;
    ctx.fillStyle = isLight ? colors.finishLight : colors.finishDark;
    ctx.beginPath();
    ctx.moveTo(farLeft, far.screenY);
    ctx.lineTo(farRight, far.screenY);
    ctx.lineTo(nearRight, near.screenY);
    ctx.lineTo(nearLeft, near.screenY);
    ctx.closePath();
    ctx.fill();
  }
}

function pickAlternatingByWorld(
  worldZ: number,
  periodMeters: number,
  light: string,
  dark: string,
): string {
  return Math.floor(worldZ / periodMeters) % 2 === 0 ? light : dark;
}

function nextPhaseBoundary(worldZ: number, periodMeters: number): number {
  return (Math.floor(worldZ / periodMeters) + 1) * periodMeters;
}

function modulo(value: number, period: number): number {
  return ((value % period) + period) % period;
}

function nextDutyBoundary(
  worldZ: number,
  cycleMeters: number,
  visibleMeters: number,
): number {
  const phase = modulo(worldZ, cycleMeters);
  const cycleStart = worldZ - phase;
  if (phase < visibleMeters) return cycleStart + visibleMeters;
  return cycleStart + cycleMeters;
}

function lerpNumber(start: number, end: number, t: number): number {
  return start + (end - start) * t;
}

function edgeAt(near: StripEdge, far: StripEdge, t: number): Pick<StripEdge, "screenX" | "screenY" | "screenW"> {
  return {
    screenX: lerpNumber(near.screenX, far.screenX, t),
    screenY: lerpNumber(near.screenY, far.screenY, t),
    screenW: lerpNumber(near.screenW, far.screenW, t),
  };
}

function drawPhasedTrapezoids(
  ctx: CanvasRenderingContext2D,
  near: StripEdge,
  far: StripEdge,
  worldNear: number,
  worldFar: number,
  widthScale: number,
  periodMeters: number,
  colorAtWorld: (worldZ: number) => string | null,
  widthOptions: { minNearHalfW?: number; minFarHalfW?: number } = {},
): void {
  const span = worldFar - worldNear;
  if (span <= 0) return;

  let cursor = worldNear;
  let guard = 0;
  while (cursor < worldFar && guard < 16) {
    const next = Math.min(worldFar, nextPhaseBoundary(cursor, periodMeters));
    const mid = (cursor + next) / 2;
    const color = colorAtWorld(mid);
    if (color) {
      const tNear = (cursor - worldNear) / span;
      const tFar = (next - worldNear) / span;
      const nearEdge = edgeAt(near, far, tNear);
      const farEdge = edgeAt(near, far, tFar);
      drawTrapezoid(
        ctx,
        color,
        nearEdge.screenX,
        nearEdge.screenY,
        Math.max(widthOptions.minNearHalfW ?? 0, nearEdge.screenW * widthScale),
        farEdge.screenX,
        farEdge.screenY,
        Math.max(widthOptions.minFarHalfW ?? 0, farEdge.screenW * widthScale),
      );
    }
    cursor = next;
    guard += 1;
  }
}

function drawDutyCycleTrapezoids(
  ctx: CanvasRenderingContext2D,
  near: StripEdge,
  far: StripEdge,
  worldNear: number,
  worldFar: number,
  widthScale: number,
  cycleMeters: number,
  visibleMeters: number,
  colorAtWorld: (worldZ: number) => string | null,
  widthOptions: { minNearHalfW?: number; minFarHalfW?: number } = {},
): void {
  const span = worldFar - worldNear;
  if (span <= 0) return;
  if (cycleMeters <= 0 || visibleMeters <= 0) return;
  if (visibleMeters >= cycleMeters) {
    drawPhasedTrapezoids(
      ctx,
      near,
      far,
      worldNear,
      worldFar,
      widthScale,
      cycleMeters,
      colorAtWorld,
      widthOptions,
    );
    return;
  }

  let cursor = worldNear;
  let guard = 0;
  while (cursor < worldFar && guard < 32) {
    const next = Math.min(worldFar, nextDutyBoundary(cursor, cycleMeters, visibleMeters));
    const mid = (cursor + next) / 2;
    const color = colorAtWorld(mid);
    if (color) {
      const tNear = (cursor - worldNear) / span;
      const tFar = (next - worldNear) / span;
      const nearEdge = edgeAt(near, far, tNear);
      const farEdge = edgeAt(near, far, tFar);
      drawTrapezoid(
        ctx,
        color,
        nearEdge.screenX,
        nearEdge.screenY,
        Math.max(widthOptions.minNearHalfW ?? 0, nearEdge.screenW * widthScale),
        farEdge.screenX,
        farEdge.screenY,
        Math.max(widthOptions.minFarHalfW ?? 0, farEdge.screenW * widthScale),
      );
    }
    cursor = next;
    guard += 1;
  }
}
